/**
 * When a post is read, and which reading counts as which.
 *
 * Plain types and pure functions, no zod — the creator's "My submissions"
 * screen labels the readings with these, and the cron decides what is due with
 * the same code, so the two can never disagree about what "the 24-hour
 * reading" means.
 *
 * ## Buckets are computed from `postPublishedAt`, never from the submission
 *
 * A creator can paste a link to a post that went up three days ago. Phase 3
 * stamped that submission's first reading `hoursSincePublish = 0` regardless,
 * which would make a 72-hour-old count look like the baseline it is measured
 * against — the growth of the whole window, invented out of an accounting
 * mistake. Everything here takes the PUBLISH instant and works out the age of
 * each reading from that; a reading taken at 73 hours is a 72-hour reading, and
 * the buckets it skipped were never taken and are recorded as missing.
 *
 * ## The window a reading may land in
 *
 * `contentSignal.ts` in the fleet attributes a reading to hour H when it was
 * taken in `[0.5H, H]` — it reads snapshots that already exist, so the risk is
 * a reading that is too YOUNG. Nexus takes its own readings on the boundary, so
 * the risk is the opposite: a tick that ran late. A reading serves bucket H
 * when its age is in `[H, H + SLACK_RATIO * H]`. Outside that it serves no
 * bucket at all and the bucket counts as missed, because recording a 40-hour
 * count in the column headed "24 hours" is the same age confound wearing the
 * other hat.
 */

/** The interim readings every submission gets, before the window closes. */
export const INTERIM_BUCKET_HOURS: readonly number[] = Object.freeze([6, 24, 48]);

/**
 * How late a reading may be and still count for its bucket, as a share of the
 * bucket's own age: 1.5 h late at hour 6, 18 h at hour 72. Generous, because
 * the cost of refusing a slightly late reading is a bucket with no number in
 * it, and the cron runs every 15 minutes so the real lag is minutes.
 */
export const READING_SLACK_RATIO = 0.25;

export type BucketKind = 'INTERIM' | 'WINDOW' | 'FINAL';

export interface MeasurementBucket {
  /** Hours since publish at which this reading is due. */
  hours: number;
  kind: BucketKind;
}

/**
 * Every reading a submission is due, in order.
 *
 * `WINDOW` is the one the money is computed from (the campaign's
 * `measurementWindowHours`, 72 by default). `FINAL` is the liveness reading at
 * the end of the holdback: its job is to confirm the post is still public at
 * the moment the earning becomes payable, which is the whole point of holding
 * the money back at all.
 */
export function measurementBuckets(
  windowHours: number,
  holdbackDays: number,
): MeasurementBucket[] {
  const buckets: MeasurementBucket[] = [];
  for (const hours of INTERIM_BUCKET_HOURS) {
    if (hours < windowHours) buckets.push({ hours, kind: 'INTERIM' });
  }
  buckets.push({ hours: windowHours, kind: 'WINDOW' });
  const finalHours = windowHours + Math.max(0, holdbackDays) * 24;
  if (finalHours > windowHours) buckets.push({ hours: finalHours, kind: 'FINAL' });
  return buckets;
}

/** Hours between two instants, as a float. NaN-safe: null in, null out. */
export function ageHours(publishedAt: string | null, at: Date): number | null {
  if (!publishedAt) return null;
  const published = Date.parse(publishedAt);
  if (!Number.isFinite(published)) return null;
  return (at.getTime() - published) / 3_600_000;
}

/** The latest instant a reading may be taken and still serve this bucket. */
export function bucketDeadlineHours(hours: number): number {
  return hours + hours * READING_SLACK_RATIO;
}

/**
 * Which bucket a reading of this age serves, or null when it serves none.
 *
 * The LARGEST bucket whose window contains the age, so the submission-time
 * reading of a three-day-old post is the 72-hour reading it actually is, and
 * not the 6-hour one it is also arithmetically inside.
 */
export function bucketForAge(
  age: number | null,
  buckets: readonly MeasurementBucket[],
): MeasurementBucket | null {
  if (age === null || !Number.isFinite(age)) return null;
  let found: MeasurementBucket | null = null;
  for (const bucket of buckets) {
    if (age + 1e-9 >= bucket.hours && age <= bucketDeadlineHours(bucket.hours) + 1e-9) {
      if (!found || bucket.hours > found.hours) found = bucket;
    }
  }
  return found;
}

/** The instant a bucket's reading becomes due. */
export function bucketDueAt(publishedAt: string, hours: number): string {
  return new Date(Date.parse(publishedAt) + hours * 3_600_000).toISOString();
}

/** What the creator's screen calls a reading. */
export function bucketLabel(hours: number, kind: BucketKind = 'INTERIM'): string {
  if (kind === 'FINAL') return 'Final check';
  if (hours % 24 === 0 && hours >= 24) {
    const days = hours / 24;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

/**
 * How many attempts a bucket gets before it is given up on.
 *
 * TWO, and then the calculator falls back to the latest successful reading
 * before the window closed. A failed read writes a FAILURE `MetricSnapshot`
 * (the unique index is partial on SUCCESS precisely so it can), and a third
 * attempt on a provider that has answered "no" twice is quota spent on an
 * answer we already have.
 */
export const BUCKET_ATTEMPTS = 2;
